import { Locale } from '@ardoq/locale';
import { ValidationStep } from './types';
import * as validationFns from './validationFunctions';

// TODO: more validators according to these types
// - ValidationStep<'string-color'>
// - ValidationStep<'string-regex', { regex: RegExp }>
// - ValidationStep<'date-before', { locale: string; comparisonDate: string }>
// - ValidationStep<'date-after', { locale: string; comparisonDate: string }>

/**
 * A function mapping validation steps to the respective implementation, and
 * returns a boolean indicating whether the validation step passed or not.
 */
export const runValidationStepUsingBuiltInValidators = (
  validator: BuiltInValidationSteps,
  value: unknown
): boolean | undefined => {
  switch (validator.id) {
    case 'string-required':
      return validationFns.requiredString(value);
    case 'min-length':
      return validationFns.minLength(value, validator.contextData.length);
    case 'max-length':
      return validationFns.maxLength(value, validator.contextData.length);
    case 'equals':
      return validationFns.equals(value, validator.contextData.value);
    case 'not-equals':
      return validationFns.notEquals(value, validator.contextData.value);
    case 'array-required':
      return validationFns.requiredArray(value);
    case 'included-in':
      return validationFns.includedIn(value, validator.contextData.array);
    case 'not-included-in':
      return validationFns.notIncludedIn(value, validator.contextData.array);
    case 'subset-of':
      return validationFns.subsetOf(value, validator.contextData.array);
    case 'string-email':
      return validationFns.isEmail(value);
    case 'boolean':
      return validationFns.isBoolean(value);
    case 'number':
      return validationFns.isNumber(value);
    case 'number-safe-int':
      return validationFns.isSafeInteger(value);
    case 'number-min':
      return validationFns.isNumberGreaterThan(
        value,
        validator.contextData.minimum
      );
    case 'number-max':
      return validationFns.isNumberLowerThan(
        value,
        validator.contextData.maximum
      );
    case 'date-before':
      return validationFns.isDateBefore(
        value,
        validator.contextData.counterpartDate,
        validator.contextData.locale
      );
    case 'date-after':
      return validationFns.isDateAfter(
        value,
        validator.contextData.counterpartDate,
        validator.contextData.locale
      );
    case 'dateOnly-ISO8601':
      return validationFns.isDateOnlyISO8601(value);
    case 'dateTime-ISO8601':
      return validationFns.isDateTimeISO8601(value);
    case 'password-complexity':
      return validationFns.passwordComplexity(value);
    case 'is-valid-url':
      return validationFns.isValidUrl(value);
    case 'date-only-range':
      return validationFns.isValidDateOnlyRange(value);
    case 'date-time-range':
      return validationFns.isValidDateTimeRange(value);
    case 'ardoq-id':
      return validationFns.isValidArdoqId(value);
    case 'tag-name':
      return validationFns.isValidTagName(value);
    case 'every-tag-name':
      return validationFns.isEveryValidTagName(value);
    default:
      validator satisfies never;
      // Return undefined to indicate that the validator is not handled
      return undefined;
  }
};

/**
 * A collection of validation steps
 */
export const validateAs = {
  requiredString: (contextData?: { message: string }) => {
    return {
      // `as const` is needed to get the string literal type to discriminate on
      id: 'string-required' as const,
      message: contextData?.message ?? 'Required',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  minLength: (contextData: { length: number; message?: string }) => {
    return {
      id: 'min-length' as const,
      contextData,
      message:
        contextData.message ??
        `Length should be more than ${contextData.length}`,
    } satisfies ValidationStep<string, typeof contextData>;
  },
  maxLength: (contextData: { length: number }) => {
    return {
      id: 'max-length' as const,
      contextData,
    } satisfies ValidationStep<string, typeof contextData>;
  },
  equals: (contextData: { value: unknown }) => {
    return {
      id: 'equals' as const,
      contextData,
    } satisfies ValidationStep<string, typeof contextData>;
  },
  notEquals: (contextData: { value: unknown; message?: string }) => {
    return {
      id: 'not-equals' as const,
      contextData,
      message: contextData.message || 'Values should not be equal',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  includedIn: (contextData: { array: unknown[]; message?: string }) => {
    return {
      id: 'included-in' as const,
      contextData,
      message: contextData.message || 'This value is not allowed',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  notIncludedIn: (contextData: { array: unknown[]; message?: string }) => {
    return {
      id: 'not-included-in' as const,
      contextData,
      message: contextData.message || 'This value is not allowed',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  subsetOf: (contextData: { array: unknown[]; message?: string }) => {
    return {
      id: 'subset-of' as const,
      contextData,
      message: contextData.message || 'Has invalid values',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  requiredArray: () => {
    return {
      id: 'array-required' as const,
    } satisfies ValidationStep<string>;
  },
  email: () => {
    return {
      id: 'string-email' as const,
    } satisfies ValidationStep<string>;
  },
  number: () => {
    return {
      id: 'number' as const,
    } satisfies ValidationStep<string>;
  },
  numberSafeInteger: () => {
    return {
      id: 'number-safe-int' as const,
    } satisfies ValidationStep<string>;
  },
  numberMin: (contextData: { minimum: number }) => {
    return {
      id: 'number-min' as const,
      contextData,
    } satisfies ValidationStep<string, typeof contextData>;
  },
  numberMax: (contextData: { maximum: number }) => {
    return {
      id: 'number-max' as const,
      contextData,
    } satisfies ValidationStep<string, typeof contextData>;
  },
  boolean: () => {
    return {
      id: 'boolean' as const,
    } satisfies ValidationStep<string>;
  },
  dateBefore: (contextData: { counterpartDate: string; locale: Locale }) => {
    return {
      id: 'date-before' as const,
      contextData,
      message: 'Start date should precede end date',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  dateAfter: (contextData: { counterpartDate: string; locale: Locale }) => {
    return {
      id: 'date-after' as const,
      contextData,
      message: 'Start date should precede end date',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  dateOnlyISO8601: () => {
    return {
      id: 'dateOnly-ISO8601' as const,
    } satisfies ValidationStep<string>;
  },
  dateTimeISO8601: () => {
    return {
      id: 'dateTime-ISO8601' as const,
    } satisfies ValidationStep<string>;
  },
  passwordComplexity: (contextData?: { message?: string }) => {
    return {
      id: 'password-complexity' as const,
      message:
        contextData?.message || 'Must contain one uppercase letter or number',
    } satisfies ValidationStep<string>;
  },
  isValidUrl: (contextData?: { message?: string }) => {
    return {
      id: 'is-valid-url' as const,
      message: contextData?.message || 'Should be a valid URL',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  dateOnlyRange: (contextData?: { message?: string }) => {
    return {
      id: 'date-only-range' as const,
      message:
        contextData?.message ||
        'Start date must be before or equal to end date when both are provided',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  dateTimeRange: (contextData?: { message?: string }) => {
    return {
      id: 'date-time-range' as const,
      message:
        contextData?.message ||
        'Start datetime must be before or equal to end datetime when both are provided',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  ardoqId: (contextData?: { message?: string }) => {
    return {
      id: 'ardoq-id' as const,
      message: contextData?.message ?? 'Must be a valid Ardoq ID',
    } satisfies ValidationStep<string, typeof contextData>;
  },
  tagName: (contextData?: { message?: string }) => {
    return {
      id: 'tag-name' as const,
      message:
        contextData?.message ?? validationFns.TAG_NAME_VALIDATION_MESSAGE,
    } satisfies ValidationStep<string, typeof contextData>;
  },
  everyTagName: (contextData?: { message?: string }) => {
    return {
      id: 'every-tag-name' as const,
      message:
        contextData?.message ?? validationFns.TAG_NAME_VALIDATION_MESSAGE,
    } satisfies ValidationStep<string, typeof contextData>;
  },
};

type BuiltInValidationSteps = ReturnType<
  (typeof validateAs)[keyof typeof validateAs]
>;
